const CRLF = Uint8Array.of(0x0d, 0x0a);
const CRLFx2 = Uint8Array.of(...CRLF, ...CRLF);
const EOF = Uint8Array.of(0x2d, 0x2d, ...CRLF);

enum State {
  PREAMBLE,
  OK,
  EPILOGUE,
}

/**
 * Parses a multipart response into multiple responses
 */
export async function* multipart(response: Response): AsyncGenerator<Response> {
  const contentType = response.headers.get('content-type') ?? '';
  const boundary = getBoundary(contentType);
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- will throw anyway
  const reader = response.body!.getReader();
  const decoder = new MultipartDecoder(boundary);
  while (true) {
    const chunk = await reader.read();
    if (chunk.done) return;
    yield* decoder.decode(chunk.value);
  }
}

/**
 * A decoder for multipart responses
 *
 * @see https://www.w3.org/Protocols/rfc1341/7_2_Multipart.html
 */
class MultipartDecoder {
  readonly #buffer = new Uint8Buffer();
  readonly #boundary: Uint8Array;
  #state = State.PREAMBLE;

  constructor(boundary: string) {
    if (!boundary) throw new Error('a boundary is required');
    this.#boundary = new TextEncoder().encode(`\r\n--${boundary}`);
  }

  *decode(array: Uint8Array): Generator<Response> {
    this.#buffer.push(array);
    let offset = 0;
    const boundary = this.#boundary;
    while (true) {
      const idx = this.#buffer.indexOf(boundary, offset);
      if (idx === -1) {
        // no boundary found, need to wait for more data
        return;
      } else if (this.#buffer.offsetEquals(CRLF, boundary.length + idx)) {
        // found a normal boundary
        if (this.#state === State.OK) yield this.#createMultipart(idx);
        else if (this.#state === State.PREAMBLE) this.#state = State.OK;
        this.#buffer.pop(idx + boundary.length + CRLF.length);
        offset = 0;
      } else if (this.#buffer.offsetEquals(EOF, boundary.length + idx)) {
        // found the closing boundary
        yield this.#createMultipart(idx);
        this.#state = State.EPILOGUE;
        this.#buffer.pop(idx + boundary.length + EOF.length);
        offset = 0;
      } else {
        // not a valid boundary, continue looking
        offset = idx + 1;
      }
    }
  }

  #createMultipart(end: number): Response {
    const separator = this.#buffer.indexOf(CRLFx2);
    const headers = new Headers();
    const lines = new TextDecoder()
      .decode(this.#buffer.subarray(0, separator))
      .split('\n');
    for (const line of lines) {
      const i = line.indexOf(':');
      if (i > 0)
        headers.append(line.slice(0, i).trim(), line.slice(i + 1).trim());
    }
    const body = this.#buffer.slice(separator + CRLFx2.length, end);
    return new Response(body, { headers });
  }
}

function getBoundary(contentType: string) {
  let boundary: string | null = null;
  for (const part of contentType.split(';')) {
    const m = /boundary="(.*)"/.exec(part);
    if (m) {
      boundary = m[1];
      break;
    }
  }
  if (boundary == null) {
    throw new Error(`no valid boundary: ${contentType}`);
  }
  return boundary;
}

/**
 * A dynamically sized Uint8 array
 */
class Uint8Buffer {
  #buffer = new Uint8Array(1024);
  #size = 0;

  /** Append the given array to this buffer */
  push(array: Uint8Array): void {
    const needed = this.#size + array.length;
    if (this.#buffer.length < needed) {
      let x = this.#buffer.length;
      while (x < needed) {
        x *= 2;
      }
      const next = new Uint8Array(x);
      next.set(this.#buffer);
      this.#buffer = next;
    }
    this.#buffer.set(array, this.#size);
    this.#size += array.length;
  }

  /** Remove the specified number of elements from the beginning of the buffer */
  pop(n: number): void {
    const view = this.subarray(n, this.#size);
    this.#buffer.set(view);
    this.#size = view.length;
  }

  /** Find the index of the first occurrence of the given array */
  indexOf(array: Uint8Array, offset = 0): number {
    for (let i = offset; i < this.#size; i++) {
      if (this.offsetEquals(array, i)) return i;
    }
    return -1;
  }

  /** Returns whether the current buffer starts with the given array  */
  offsetEquals(other: Uint8Array, offset = 0): boolean {
    if (offset > this.#buffer.length - other.length) return false;
    for (let i = 0; i < other.length; i++) {
      if (this.#buffer[offset + i] !== other[i]) return false;
    }
    return true;
  }

  subarray(start?: number, end = Number.MAX_SAFE_INTEGER): Uint8Array {
    return this.#buffer.subarray(start, Math.min(end, this.#size));
  }

  slice(start?: number, end = Number.MAX_SAFE_INTEGER): Uint8Array {
    return this.#buffer.slice(start, Math.min(end, this.#size));
  }
}
